import React, { useState, useEffect } from 'react';
import { Modal, Row, Col, Badge, Form, Button, Spinner, ProgressBar } from 'react-bootstrap';
import gql from 'graphql-tag';
import * as Cookies from "es-cookie";
import './_admissionForms.scss';

const GET_ENQUIRY_FORM_FIELDS = gql`
  query ($form_id: Int!) {
    getEnquiryFormFields(form_id: $form_id) {
      id
      field_name
      field_key
      field_type_id
      field_type
      options
      is_required
      value_length
      order_no
    }
  }
`;

const UPDATE_ENQUIRY_SUBMISSION = gql`
  mutation ($input: UpdateEnquirySubmissionInput!) {
    updateEnquirySubmission(input: $input)
  }
`;

const GET_SUBMITTED_ADMISSION_DETAILS = gql`
  query ($enquiry_form_master_id: Int!) {
    getSubmittedAdmissionDetails(enquiry_form_master_id: $enquiry_form_master_id) {
      student_id
      admission_no
      submitted_fields {
        field_id
        field_name
        field_type_name
        field_value
      }
    }
  }
`;

// Helper to parse options from various formats (JSON array or comma-separated string)
const getOptions = (options) => {
    if (!options) return [];
    if (Array.isArray(options)) {
        return options.map(opt => typeof opt === 'string' ? { label: opt, value: opt } : opt);
    }
    try {
        const parsed = JSON.parse(options);
        if (Array.isArray(parsed)) {
            return parsed.map(opt => typeof opt === 'string' ? { label: opt, value: opt } : opt);
        }
    } catch (e) {
        // Fallback to comma separated
    }
    return options.toString().split(',').map(o => ({ label: o.trim(), value: o.trim() })).filter(o => o.label);
};

const StudentProfileModal = ({ show, onHide, student, hasExams, client, formId, onUpdate, branches }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [extraFields, setExtraFields] = useState({});
    const [formFieldsConfig, setFormFieldsConfig] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [admissionDetails, setAdmissionDetails] = useState(null);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (show && student) {
            setIsEditing(false);
            setErrors({});
            setFormData({
                id: student.id,
                student_name: student.student_name || '',
                student_mobile: student.student_mobile || '',
                student_email: student.student_email || '',
                gender: student.gender || '',
                dob: formatDateForInput(student.dob),
                enquiry_course_id: student.enquiry_course_id || '',
                branch_id: student.branch_id || ''
            });
            
            // Parse extra_details safely
            let rawDetails = {};
            try {
                if (student.extra_details) {
                    rawDetails = typeof student.extra_details === 'string'
                        ? JSON.parse(student.extra_details)
                        : student.extra_details;
                }
            } catch (e) {
                console.warn("Failed to parse extra_details", e);
            }
            
            const extra = {};
            if (rawDetails && typeof rawDetails === 'object' && !Array.isArray(rawDetails)) {
                Object.entries(rawDetails).forEach(([key, value]) => {
                    extra[key] = value;
                });
            } else if (Array.isArray(rawDetails)) {
                rawDetails.forEach(item => {
                    if (item) {
                        const key = item.label || item.field_name || item.name || "";
                        extra[key] = item.value || item.field_value;
                    }
                });
            }
            setExtraFields(extra);

            // Use the student's own form when no single formId is provided
            // (e.g. the "All Enquiry Students" view spans multiple forms).
            const effectiveFormId = formId || student.enquiry_form_id;
            if (client && effectiveFormId) {
                fetchFormFields(effectiveFormId);
            } else {
                setFormFieldsConfig([]);
            }
            if (student.is_admitted === 1 && client) {
                fetchAdmissionDetails(student.id);
            } else {
                setAdmissionDetails(null);
            }
        }
    }, [show, student]);

    const fetchAdmissionDetails = async (enquiryId) => {
        try {
            const { data } = await client.query({
                query: GET_SUBMITTED_ADMISSION_DETAILS,
                variables: { enquiry_form_master_id: parseInt(enquiryId) },
                fetchPolicy: 'network-only'
            });
            if (data && data.getSubmittedAdmissionDetails) {
                setAdmissionDetails(data.getSubmittedAdmissionDetails);
            }
        } catch (err) {
            console.error("Error fetching admission details:", err);
        }
    };

    const fetchFormFields = async (formIdToUse) => {
        const fid = formIdToUse || formId;
        setLoading(true);
        try {
            const { data } = await client.query({
                query: GET_ENQUIRY_FORM_FIELDS,
                variables: { form_id: parseInt(fid) },
                fetchPolicy: 'network-only'
            });
            if (data && data.getEnquiryFormFields) {
                // Keep the form's saved order_no order.
                const ordered = [...data.getEnquiryFormFields].sort(
                    (a, b) => (a.order_no || 0) - (b.order_no || 0)
                );
                setFormFieldsConfig(ordered);
            }
        } catch (err) {
            console.error("Error fetching form fields:", err);
        }
        setLoading(false);
    };

    const handleSave = async () => {
        // ── Validation (same rules as Add Enquiry) ──
        const MASTER_KEYS = ["student_name", "student_mobile", "student_email", "dob", "gender", "enquiry_course_id"];
        const reqByKey = {};
        const dynFields = [];
        formFieldsConfig.forEach((f) => {
            if (f.field_key && MASTER_KEYS.includes(f.field_key)) {
                reqByKey[f.field_key] = !!f.is_required;
            } else {
                dynFields.push(f);
            }
        });
        const isReq = (key, dflt) => (reqByKey[key] === undefined ? dflt : reqByKey[key]);

        const newErrors = {};
        if (isReq("student_name", true) && !String(formData.student_name || "").trim()) {
            newErrors.student_name = "Name is required";
        }
        const mobile = String(formData.student_mobile || "").replace(/\D/g, "");
        if (isReq("student_mobile", true) && !mobile) {
            newErrors.student_mobile = "Mobile is required";
        } else if (mobile && mobile.length !== 10) {
            newErrors.student_mobile = "Enter a valid 10 digit mobile";
        }
        if (isReq("enquiry_course_id", true) && !formData.enquiry_course_id) {
            newErrors.enquiry_course_id = "Enquiry Class is required";
        }
        // Mandatory dynamic fields
        dynFields.forEach((f) => {
            if (f.is_required) {
                const v = extraFields[f.field_name];
                if (v === undefined || v === null || String(v).trim() === "") {
                    newErrors[`field_${f.id}`] = `${f.field_name} is required`;
                }
            }
        });

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        setErrors({});

        setSaving(true);
        try {
            const userId = Cookies.get("user_id") ? parseInt(Cookies.get("user_id")) : 0;
            
            const input = {
                id: formData.id,
                student_name: formData.student_name,
                student_mobile: formData.student_mobile,
                student_email: formData.student_email,
                gender: formData.gender,
                dob: formData.dob || null,
                enquiry_course_id: formData.enquiry_course_id ? parseInt(formData.enquiry_course_id) : null,
                branch_id: formData.branch_id ? String(formData.branch_id) : null,
                extra_details: extraFields,
                updated_by: userId
            };

            await client.mutate({
                mutation: UPDATE_ENQUIRY_SUBMISSION,
                variables: { input }
            });
            
            setIsEditing(false);
            if (onUpdate) onUpdate();
            if (onHide) onHide(); // close the popup after a successful save
        } catch (err) {
            console.error("Error updating student:", err);
            alert("Failed to update student details. " + err.message);
        }
        setSaving(false);
    };

    if (!student) return null;

    const formatDateForInput = (dateStr) => {
        if (!dateStr) return '';
        try {
            let d = new Date(dateStr);
            if (isNaN(d.getTime()) && !isNaN(Number(dateStr))) d = new Date(Number(dateStr));
            if (isNaN(d.getTime())) return '';
            return d.toISOString().split('T')[0];
        } catch { return ''; }
    };

    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return 'N/A';
        try {
            let d = new Date(dateStr);
            if (isNaN(d.getTime()) && !isNaN(Number(dateStr))) d = new Date(Number(dateStr));
            if (isNaN(d.getTime())) return dateStr;
            return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
        } catch { return dateStr; }
    };

    const studentName = formData.student_name || student.student_name || student.name || "N/A";
    const initials = studentName.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    const dob = formatDateDisplay(formData.dob || student.dob);
    
    const testStatus = (student.test_status || "").toLowerCase();
    const isAdmitted = student.is_admitted === 1;
    const testPercentage = (student.test_score && student.total_marks) 
        ? Math.round((student.test_score / student.total_marks) * 100) 
        : null;

    // Dynamic Fields Preparation
    // Find the Enquiry Class field by its stable key (falls back to name) so the
    // course dropdown always shows, even if the field's label was edited.
    const courseField = formFieldsConfig.find(f => f.field_key === 'enquiry_course_id')
        || formFieldsConfig.find(f => ['enquiry class', 'course name', 'course'].includes((f.field_name || '').toLowerCase()));
    
    // Comprehensive list of standard fields to exclude from "Extra Details"
    const standardFieldExclusions = [
        "name", "student name", "full name",
        "mobile", "mobile number", "phone", "phone number", "contact number", "primary contact number",
        "date of birth", "dob",
        "gender",
        "email", "email address",
        "enquiry class", "course name", "class"
    ];
    
    // Exclude master/base fields by their stable key (their label can be edited,
    // so name-based exclusion alone would let a renamed base field show twice).
    const MASTER_KEYS = ["student_name", "student_mobile", "student_email", "dob", "gender", "enquiry_course_id"];
    const dynamicFields = formFieldsConfig.filter(f =>
        !(f.field_key && MASTER_KEYS.includes(f.field_key)) &&
        !standardFieldExclusions.includes((f.field_name || "").toLowerCase())
    );

    // Use the form's configured base-field names/required/length (keyed by field_key).
    const baseLabelByKey = {};
    const baseRequiredByKey = {};
    const baseLengthByKey = {};
    formFieldsConfig.forEach((f) => {
        if (f.field_key) {
            baseLabelByKey[f.field_key] = f.field_name;
            baseRequiredByKey[f.field_key] = !!f.is_required;
            baseLengthByKey[f.field_key] = f.value_length;
        }
    });
    const baseLabel = (key, fallback) => baseLabelByKey[key] || fallback;
    // Master fields are required by convention (name/mobile/enquiry class) unless config says otherwise.
    const baseRequired = (key, dflt) => (baseRequiredByKey[key] === undefined ? dflt : baseRequiredByKey[key]);
    const reqMark = (key, dflt) => (baseRequired(key, dflt) ? <span className="text-danger ml-1">*</span> : null);

    // Filter out academic stuff and standard fields from display
    const displayExtraFields = Object.entries(extraFields).filter(([key, val]) => {
        if (!val || String(val).trim() === "") return false;
        const nKey = key.toLowerCase();
        
        if (standardFieldExclusions.includes(nKey)) return false;
        
        const academicKeywords = ['school', 'cgpa', 'percentage', 'marks', 'gpa', 'ssc', '10th', 'inter', 'state', 'district', 'city', 'pincode', 'zipcode'];
        return !academicKeywords.some(keyword => nKey.includes(keyword));
    });

    return (
        <Modal show={show} onHide={onHide} size="lg" centered scrollable className="af-student-profile-redesign">
            <style>{`
                .af-student-profile-redesign .modal-content { border-radius: 14px; overflow: hidden; border: 1px solid #e2e8f0; box-shadow: 0 20px 40px -12px rgba(15, 23, 42, 0.18); min-height: 500px; }
                .af-profile-main { padding: 0; background: #f8fafc; height: 100%; overflow-y: auto; position: relative; }

                .af-modal-close { position: absolute; top: 16px; right: 16px; width: 34px; height: 34px; border-radius: 50%; background: #fff; border: 1px solid #e2e8f0; color: #64748b; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.2s; z-index: 20; font-size: 14px; }
                .af-modal-close:hover { background: #f1f5f9; color: #0f172a; border-color: #cbd5e1; }

                .af-hero { background: #fff; padding: 28px 36px; border-bottom: 1px solid #e2e8f0; }
                .af-body-pad { padding: 24px 36px 36px; }

                .af-header-section { display: flex; align-items: flex-start; justify-content: space-between; }
                .af-avatar-circle { width: 64px; height: 64px; background: #f1f5f9; color: #475569; border: 1px solid #e2e8f0; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 22px; font-weight: 700; flex-shrink: 0; }
                .af-header-name { color: #0f172a; font-weight: 700; font-size: 20px; }
                .af-header-meta { color: #64748b; font-size: 13px; font-weight: 500; }

                .af-status-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 12px; border-radius: 999px; font-size: 12px; font-weight: 600; letter-spacing: 0.2px; border: 1px solid #e2e8f0; background: #f8fafc; color: #475569; }
                .af-status-admitted, .af-status-completed, .af-status-assigned, .af-status-active { background: #f8fafc; color: #475569; border-color: #e2e8f0; }

                .af-card-box { background: #fff; border-radius: 12px; border: 1px solid #e2e8f0; padding: 24px 28px; margin-bottom: 16px; }
                .af-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; border-bottom: 1px solid #f1f5f9; padding-bottom: 12px; }
                .af-card-title { font-size: 16px; font-weight: 700; color: #0f172a; margin: 0; display: flex; align-items: center; }
                .af-card-title i { color: #64748b; margin-right: 10px; font-size: 14px; }

                .af-detail-label { font-size: 11px; text-transform: uppercase; color: #64748b; font-weight: 600; letter-spacing: 0.5px; margin-bottom: 6px; }
                .af-detail-value { font-size: 15px; color: #1e293b; font-weight: 500; margin-bottom: 20px; }

                .af-btn-edit { background: #fff; color: #0f172a; border: 1px solid #cbd5e1; font-weight: 600; font-size: 14px; padding: 8px 16px; border-radius: 8px; transition: all 0.2s; display: inline-flex; align-items: center; }
                .af-btn-edit:hover { background: #f1f5f9; border-color: #94a3b8; }

                .af-btn-cancel { background: #fff; color: #475569; border: 1px solid #cbd5e1; font-weight: 600; font-size: 14px; padding: 8px 18px; border-radius: 8px; transition: all 0.2s; }
                .af-btn-cancel:hover { background: #f1f5f9; }
                .af-btn-save { background: #0f172a; color: #fff; border: 1px solid #0f172a; font-weight: 600; font-size: 14px; padding: 8px 20px; border-radius: 8px; transition: all 0.2s; }
                .af-btn-save:hover { background: #1e293b; color: #fff; }
                .af-btn-save:disabled { opacity: 0.6; }

                .af-custom-input { border-radius: 8px; border: 1px solid #cbd5e1; padding: 10px 14px; font-size: 15px; transition: all 0.2s; background: #fff; }
                .af-custom-input:focus { border-color: #64748b; box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.12); background: #fff; }
                .af-form-label { font-size: 13px; font-weight: 600; color: #475569; margin-bottom: 8px; }
            `}</style>
            
            <Modal.Body className="p-0">
                <div className="af-profile-main">
                    <button type="button" className="af-modal-close" onClick={onHide} title="Close" aria-label="Close">
                        <i className="fas fa-times" />
                    </button>

                    <div className="af-hero">
                        <div className="af-header-section flex-wrap">
                            <div className="d-flex align-items-center mb-3 mb-md-0">
                                <div className="af-avatar-circle mr-4">{initials}</div>
                                <div>
                                    <h3 className="af-header-name m-0 d-flex align-items-center flex-wrap">
                                        {studentName}
                                        <span className="ml-0 ml-sm-3 mt-2 mt-sm-0">
                                            {isAdmitted ? (
                                                <span className="af-status-pill af-status-admitted"><i className="fas fa-check-circle" /> Admitted</span>
                                            ) : testStatus === "completed" ? (
                                                <span className="af-status-pill af-status-completed"><i className="fas fa-clipboard-check" /> Test Completed</span>
                                            ) : testStatus === "assigned" ? (
                                                <span className="af-status-pill af-status-assigned"><i className="fas fa-clock" /> Test Assigned</span>
                                            ) : (
                                                <span className="af-status-pill af-status-active"><i className="fas fa-circle" style={{ fontSize: 8 }} /> Enquiry Active</span>
                                            )}
                                        </span>
                                    </h3>
                                    <div className="af-header-meta mt-2">
                                        Registered: {formatDateDisplay(student.inserted_at)}
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex w-100 w-md-auto justify-content-end mt-3 mt-md-0">
                                {!isEditing ? (
                                    <button className="af-btn-edit" onClick={() => setIsEditing(true)}>
                                        <i className="fas fa-edit mr-2" /> Edit Details
                                    </button>
                                ) : (
                                    <>
                                        <button className="af-btn-cancel mr-2" onClick={() => { setIsEditing(false); setErrors({}); }}>Cancel</button>
                                        <button className="af-btn-save" onClick={handleSave} disabled={saving}>
                                            {saving ? <Spinner size="sm" animation="border" /> : <><i className="fas fa-check mr-2" />Save Changes</>}
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="af-body-pad">

                    {/* Exam Credentials if assigned but not admitted */}
                    {(student.username || student.password) && !isAdmitted && (
                        <div className="alert alert-info mb-4 d-flex align-items-center shadow-sm" style={{ borderRadius: '12px', border: '1px solid #bae6fd', backgroundColor: '#f0f9ff' }}>
                            <i className="fas fa-info-circle mr-3 text-info" style={{ fontSize: '24px' }}></i>
                            <div>
                                <div className="font-weight-bold mb-1 text-info" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Exam Credentials</div>
                                <div className="text-dark">
                                    <span className="mr-4">Username: <strong>{student.username}</strong></span>
                                    <span>Password: <strong>{student.password}</strong></span>
                                </div>
                            </div>
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Loading configuration...</p>
                        </div>
                    ) : (
                        <>
                            {/* Personal Information */}
                            <div className="af-card-box">
                                <div className="af-card-header">
                                    <h5 className="af-card-title"><i className="fas fa-user-circle" /> Personal Information</h5>
                                </div>
                                
                                {!isEditing ? (
                                    <Row>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">{baseLabel("student_name", "Student Name")}</div>
                                            <div className="af-detail-value">{studentName}</div>
                                        </Col>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">{baseLabel("student_mobile", "Primary Contact Number")}</div>
                                            <div className="af-detail-value">{student.student_mobile || "N/A"}</div>
                                        </Col>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">{baseLabel("dob", "Date of Birth")}</div>
                                            <div className="af-detail-value">{dob}</div>
                                        </Col>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">{baseLabel("enquiry_course_id", "Enquiry Class / Course")}</div>
                                            <div className="af-detail-value text-primary font-weight-bold">{student.enquiry_courses || "N/A"}</div>
                                        </Col>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">Branch</div>
                                            <div className="af-detail-value">{student.branch_name || "N/A"}</div>
                                        </Col>
                                        {/* Additional fields continue in the same row */}
                                        {dynamicFields.map((f) => {
                                            const raw = extraFields[f.field_name];
                                            const val = (raw === undefined || raw === null || String(raw).trim() === "")
                                                ? "N/A"
                                                : (Array.isArray(raw) ? raw.join(", ") : String(raw));
                                            return (
                                                <Col sm={6} md={4} key={f.id}>
                                                    <div className="af-detail-label">{f.field_name}</div>
                                                    <div className="af-detail-value">{val}</div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                ) : (
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="af-form-label">{baseLabel("student_name", "Student Name")}{reqMark("student_name", true)}</Form.Label>
                                                <Form.Control className={`af-custom-input ${errors.student_name ? "is-invalid" : ""}`} type="text" maxLength={baseLengthByKey.student_name || undefined} value={formData.student_name} onChange={(e) => setFormData({...formData, student_name: e.target.value})} />
                                                {errors.student_name && <small className="text-danger">{errors.student_name}</small>}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="af-form-label">{baseLabel("student_mobile", "Primary Contact Number")}{reqMark("student_mobile", true)}</Form.Label>
                                                <Form.Control className={`af-custom-input ${errors.student_mobile ? "is-invalid" : ""}`} type="text" maxLength={baseLengthByKey.student_mobile || 10} value={formData.student_mobile} onChange={(e) => setFormData({...formData, student_mobile: e.target.value.replace(/\D/g, "")})} />
                                                {errors.student_mobile && <small className="text-danger">{errors.student_mobile}</small>}
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="af-form-label">{baseLabel("dob", "Date of Birth")}{reqMark("dob", false)}</Form.Label>
                                                <Form.Control className="af-custom-input" type="date" value={formData.dob} onChange={(e) => setFormData({...formData, dob: e.target.value})} />
                                            </Form.Group>
                                        </Col>
                                        {courseField && (
                                            <Col md={6}>
                                                <Form.Group>
                                                    <Form.Label className="af-form-label">{baseLabel("enquiry_course_id", "Enquiry Class")}{reqMark("enquiry_course_id", true)}</Form.Label>
                                                    <Form.Control className={`af-custom-input ${errors.enquiry_course_id ? "is-invalid" : ""}`} as="select" value={formData.enquiry_course_id} onChange={(e) => setFormData({...formData, enquiry_course_id: e.target.value})}>
                                                        <option value="">Select Class</option>
                                                         {getOptions(courseField.options).map((opt, i) => (
                                                             <option key={i} value={opt.value || opt}>{opt.label || opt}</option>
                                                         ))}
                                                    </Form.Control>
                                                    {errors.enquiry_course_id && <small className="text-danger">{errors.enquiry_course_id}</small>}
                                                </Form.Group>
                                            </Col>
                                        )}
                                        <Col md={6}>
                                            <Form.Group>
                                                <Form.Label className="af-form-label">Branch</Form.Label>
                                                <Form.Control className="af-custom-input" as="select" value={formData.branch_id} onChange={(e) => setFormData({...formData, branch_id: e.target.value})}>
                                                    <option value="">Select Branch</option>
                                                    {branches && branches.map(b => (
                                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                                    ))}
                                                </Form.Control>
                                            </Form.Group>
                                        </Col>
                                        {/* Additional fields continue in the same row */}
                                             {dynamicFields.map(f => {
                                                const typeId = parseInt(f.field_type_id);
                                                const fieldType = (f.field_type || '').toLowerCase();
                                                
                                                return (
                                                    <Col md={6} key={f.id}>
                                                        <Form.Group>
                                                            <Form.Label className="af-form-label">{f.field_name}{f.is_required && <span className="text-danger ml-1">*</span>}</Form.Label>
                                                            {typeId === 4 || fieldType === 'dropdown' ? (
                                                                <Form.Control className="af-custom-input" as="select" value={extraFields[f.field_name] || ''} onChange={(e) => setExtraFields({...extraFields, [f.field_name]: e.target.value})}>
                                                                    <option value="">Select Option</option>
                                                                    {getOptions(f.options).map((opt, i) => (
                                                                        <option key={i} value={opt.value || opt}>{opt.label || opt}</option>
                                                                    ))}
                                                                </Form.Control>
                                                            ) : typeId === 5 || fieldType === 'radio' ? (
                                                                <div className="d-flex flex-wrap gap-3 mt-2">
                                                                    {getOptions(f.options).map((opt, i) => (
                                                                        <label key={i} className="mr-3 d-flex align-items-center mb-0" style={{ cursor: 'pointer', gap: '6px' }}>
                                                                            <input 
                                                                                type="radio" 
                                                                                name={`radio_${f.id}`} 
                                                                                value={opt.value || opt} 
                                                                                checked={String(extraFields[f.field_name]) === String(opt.value || opt)}
                                                                                onChange={(e) => setExtraFields({...extraFields, [f.field_name]: e.target.value})}
                                                                            />
                                                                            <span style={{ fontSize: '14px', color: '#475569' }}>{opt.label || opt}</span>
                                                                        </label>
                                                                    ))}
                                                                </div>
                                                            ) : typeId === 7 || fieldType === 'checkbox' ? (
                                                                <div className="d-flex flex-wrap gap-3 mt-2">
                                                                    {getOptions(f.options).map((opt, i) => {
                                                                        const val = String(extraFields[f.field_name] || '');
                                                                        const currentValues = val ? val.split(',').map(v => v.trim()) : [];
                                                                        const optVal = String(opt.value || opt);
                                                                        
                                                                        return (
                                                                            <label key={i} className="mr-3 d-flex align-items-center mb-0" style={{ cursor: 'pointer', gap: '6px' }}>
                                                                                <input 
                                                                                    type="checkbox" 
                                                                                    checked={currentValues.includes(optVal)}
                                                                                    onChange={(e) => {
                                                                                        let newValues = [...currentValues];
                                                                                        if (e.target.checked) {
                                                                                            if (!newValues.includes(optVal)) newValues.push(optVal);
                                                                                        } else {
                                                                                            newValues = newValues.filter(v => v !== optVal);
                                                                                        }
                                                                                        setExtraFields({...extraFields, [f.field_name]: newValues.join(', ')});
                                                                                    }}
                                                                                />
                                                                                <span style={{ fontSize: '14px', color: '#475569' }}>{opt.label || opt}</span>
                                                                            </label>
                                                                        );
                                                                    })}
                                                                </div>
                                                            ) : typeId === 2 || fieldType === 'textarea' ? (
                                                                <Form.Control
                                                                    className="af-custom-input"
                                                                    as="textarea"
                                                                    rows={3}
                                                                    maxLength={f.value_length || undefined}
                                                                    value={extraFields[f.field_name] || ''}
                                                                    onChange={(e) => setExtraFields({...extraFields, [f.field_name]: f.value_length ? e.target.value.slice(0, f.value_length) : e.target.value})}
                                                                />
                                                            ) : (
                                                                <Form.Control
                                                                    className="af-custom-input"
                                                                    type={typeId === 3 || fieldType === 'number' ? 'number' : (typeId === 6 || fieldType === 'date' ? 'date' : 'text')}
                                                                    maxLength={f.value_length || undefined}
                                                                    value={extraFields[f.field_name] || ''}
                                                                    onChange={(e) => setExtraFields({...extraFields, [f.field_name]: f.value_length ? e.target.value.slice(0, f.value_length) : e.target.value})}
                                                                />
                                                            )}
                                                            {errors[`field_${f.id}`] && <small className="text-danger">{errors[`field_${f.id}`]}</small>}
                                                        </Form.Group>
                                                    </Col>
                                                );
                                             })}
                                        </Row>
                                )}
                            </div>

                            {/* Test Performance (Read Only) */}
                            {testStatus === "completed" && !isEditing && (
                                <div className="af-card-box border-primary" style={{ backgroundColor: 'rgba(59, 130, 246, 0.03)' }}>
                                    <div className="af-card-header border-bottom-0 pb-0">
                                        <h5 className="af-card-title text-primary"><i className="fas fa-chart-line" /> Test Performance</h5>
                                    </div>
                                    <div className="pt-3">
                                        <Row className="align-items-center">
                                            <Col md={4} className="text-center border-right">
                                                <div className="h1 mb-0 font-weight-bold text-primary">{student.test_score}</div>
                                                <div className="text-muted small text-uppercase font-weight-bold">Marks Gained / {student.total_marks}</div>
                                            </Col>
                                            <Col md={8} className="pl-md-5 mt-4 mt-md-0">
                                                <div className="d-flex justify-content-between mb-2">
                                                    <span className="small font-weight-bold text-dark">Performance Index</span>
                                                    <span className="small font-weight-bold text-primary">{testPercentage}% Scored</span>
                                                </div>
                                                <ProgressBar now={testPercentage} variant="primary" style={{ height: '10px', borderRadius: '5px' }} />
                                            </Col>
                                        </Row>
                                    </div>
                                </div>
                            )}
                            
                            {/* Admission Details */}
                            {isAdmitted && !isEditing && admissionDetails && (
                                <div className="af-card-box border-success" style={{ backgroundColor: 'rgba(16, 185, 129, 0.03)' }}>
                                    <div className="af-card-header border-bottom-0 pb-0 mb-3">
                                        <h5 className="af-card-title text-success"><i className="fas fa-file-signature" /> Admission Details</h5>
                                    </div>
                                    <Row>
                                        <Col sm={6} md={4}>
                                            <div className="af-detail-label">Admission Number</div>
                                            <div className="af-detail-value font-weight-bold text-dark">{admissionDetails.admission_no || "N/A"}</div>
                                        </Col>
                                        {admissionDetails.submitted_fields && admissionDetails.submitted_fields.map((f, idx) => {
                                            // Format field value if it's a JSON array string
                                            let valDisplay = f.field_value;
                                            try {
                                                if (f.field_value && f.field_value.startsWith('[')) {
                                                    const parsed = JSON.parse(f.field_value);
                                                    if (Array.isArray(parsed)) {
                                                        valDisplay = parsed.join(', ');
                                                    }
                                                }
                                            } catch (e) {}
                                            
                                            return (
                                                <Col sm={6} md={4} key={idx}>
                                                    <div className="af-detail-label">{f.field_name}</div>
                                                    <div className="af-detail-value">{valDisplay || "N/A"}</div>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}
                        </>
                    )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default StudentProfileModal;
